import { c as _c } from "react/compiler-runtime";
import { TooltipProvider } from '@newfront-insurance/core-ui/v2';
import type { TRPCClientHooks } from '@newfront-insurance/data-layer-client';
import { HTTPError, NextApiErrorBoundary, isTRPCClientError } from '@newfront-insurance/data-layer-client';
import { NewfrontApolloProvider } from '@newfront-insurance/graphql-frontend';
import { AuthProviderType, SSRBoundary, type AuthProviderContext, type AuthProviderProps, LoginBoundary } from '@newfront-insurance/next-auth';
import type { LoginBoundaryProps } from '@newfront-insurance/next-auth';
import { RouterProvider } from '@newfront-insurance/next-router-provider';
import type { Provider } from '@newfront-insurance/react-provision';
import type { ConfigType } from '@newfront-insurance/shared-public-config';
import type { QueryObserverOptions } from '@tanstack/react-query';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type AnyRouter } from '@trpc/server';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import type { JSX } from 'react';
import { type ReactNode } from 'react';
import { Suspense } from 'react';
import * as React from 'react';
import { AnalyticsProvider } from './providers/analytics';
import { AuthNextSwrProvider } from './providers/auth-swr';
import { ConditionalAuthProvider } from './providers/conditional-auth-provider';
import { DatadogWrapper } from './providers/datadog-wrapper';
import { FeatureFlagProvider } from './providers/feature-flags';
export interface DdRumConfig {
  applicationId: string;
  clientToken: string;
  appName: string;
}
interface AppProvidersProps {
  children: ReactNode;
  config: ConfigType;
  authProvider: Provider<AuthProviderContext, AuthProviderProps>;
  authProviderType?: AuthProviderType;
  withLoginBoundary?: boolean;
  customLoginBoundary?: React.ComponentType<LoginBoundaryProps>;
  trpcProvider: Provider<TRPCClientHooks<AnyRouter>>;
  useConditionalAuthProvider: boolean;
  analyticsAppName: string;
}
const NotificationProvider = dynamic(async () => {
  const module = await import('@newfront-insurance/next-notifications');
  return module.NotificationProvider;
}, {
  ssr: false
});
function ReactNotificationProvider(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <NotificationProvider>{children}</NotificationProvider>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
const RETRYABLE_STATUS_CODES = [418, 502, 503, 504];
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      throwOnError: (_error, query) => {
        return !query.state.data && (query.options as QueryObserverOptions).suspense === true;
      },
      retry(failureCount, error) {
        if (failureCount >= 3) {
          return false;
        }
        if (isTRPCClientError(error) && error.cause instanceof HTTPError) {
          const statusCode = error.cause.response.status;
          return RETRYABLE_STATUS_CODES.includes(statusCode);
        }
        return true;
      }
    }
  }
});

/**
 * Used to apply a custom name and service to a view
 */
export function GeneralAppProviders(t0) {
  const $ = _c(39);
  const {
    children,
    config,
    authProvider: AuthProvider,
    trpcProvider: TRPCProvider,
    authProviderType: t1,
    withLoginBoundary: t2,
    customLoginBoundary: t3,
    useConditionalAuthProvider,
    analyticsAppName
  } = t0;
  const authProviderType = t1 === undefined ? AuthProviderType.KEYCLOAK_JS : t1;
  const withLoginBoundary = t2 === undefined ? true : t2;
  const customLoginBoundary = t3 === undefined ? undefined : t3;
  let t4;
  if ($[0] !== config.API.AUTH) {
    t4 = <Head><link rel="preconnect" href={config.API.AUTH} /></Head>;
    $[0] = config.API.AUTH;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== children) {
    t5 = <TooltipProvider>{children}</TooltipProvider>;
    $[2] = children;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== TRPCProvider || $[5] !== t5) {
    t6 = <TRPCProvider>{t5}</TRPCProvider>;
    $[4] = TRPCProvider;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== AuthProvider || $[8] !== analyticsAppName || $[9] !== config || $[10] !== t6) {
    t7 = <AnalyticsProvider authProvider={AuthProvider} config={config} analyticsAppName={analyticsAppName}>{t6}</AnalyticsProvider>;
    $[7] = AuthProvider;
    $[8] = analyticsAppName;
    $[9] = config;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== AuthProvider || $[13] !== t7) {
    t8 = <AuthNextSwrProvider authProvider={AuthProvider}>{t7}</AuthNextSwrProvider>;
    $[12] = AuthProvider;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== AuthProvider || $[16] !== t8) {
    t9 = <DatadogWrapper authProvider={AuthProvider}>{t8}</DatadogWrapper>;
    $[15] = AuthProvider;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== AuthProvider || $[19] !== config || $[20] !== t9) {
    t10 = <Suspense fallback={null}><QueryClientProvider client={queryClient}><FeatureFlagProvider config={config} authProvider={AuthProvider}>{t9}</FeatureFlagProvider></QueryClientProvider></Suspense>;
    $[18] = AuthProvider;
    $[19] = config;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== AuthProvider || $[23] !== config || $[24] !== t10) {
    t11 = <ReactNotificationProvider><NewfrontApolloProvider authProvider={AuthProvider} config={config}>{t10}</NewfrontApolloProvider></ReactNotificationProvider>;
    $[22] = AuthProvider;
    $[23] = config;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== AuthProvider || $[27] !== customLoginBoundary || $[28] !== t11 || $[29] !== withLoginBoundary) {
    t12 = <LoginBoundary authProvider={AuthProvider} isEnabled={withLoginBoundary} CustomLoginBoundary={customLoginBoundary}>{t11}</LoginBoundary>;
    $[26] = AuthProvider;
    $[27] = customLoginBoundary;
    $[28] = t11;
    $[29] = withLoginBoundary;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== AuthProvider || $[32] !== authProviderType || $[33] !== t12 || $[34] !== useConditionalAuthProvider) {
    t13 = <SSRBoundary><RouterProvider><ConditionalAuthProvider useConditionalAuthProvider={useConditionalAuthProvider} authProvider={AuthProvider} authProviderType={authProviderType}>{t12}</ConditionalAuthProvider></RouterProvider></SSRBoundary>;
    $[31] = AuthProvider;
    $[32] = authProviderType;
    $[33] = t12;
    $[34] = useConditionalAuthProvider;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t13 || $[37] !== t4) {
    t14 = <NextApiErrorBoundary>{t4}{t13}</NextApiErrorBoundary>;
    $[36] = t13;
    $[37] = t4;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  return t14;
}